import { useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { AlertCircle, CheckCircle2, X } from "lucide-react";

interface ParsedInvoice {
  payment_amount: number | null;
  payment_due_date: string | null;
  supplier_name: string | null;
  supplier_email: string | null;
}

/** Submitted form data (all fields filled, no nulls) */
export type ManualInputSubmitData = {
  payment_amount: number;
  payment_due_date: string;
  supplier_name: string;
  supplier_email: string;
};

interface ManualInputFormProps {
  parsedData: ParsedInvoice;
  onSubmit: (data: ManualInputSubmitData) => void;
  onCancel: () => void;
}

export function ManualInputForm({ parsedData, onSubmit, onCancel }: ManualInputFormProps) {
  const [formData, setFormData] = useState({
    payment_amount: parsedData.payment_amount?.toString() || "",
    payment_due_date: parsedData.payment_due_date || "",
    supplier_name: parsedData.supplier_name || "",
    supplier_email: parsedData.supplier_email || "",
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  const validate = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.payment_amount || parseFloat(formData.payment_amount) <= 0) {
      newErrors.payment_amount = "Please enter a valid payment amount";
    }
    if (!formData.payment_due_date) {
      newErrors.payment_due_date = "Please enter the payment due date";
    }
    if (!formData.supplier_name.trim()) {
      newErrors.supplier_name = "Please enter the supplier name";
    }
    if (!formData.supplier_email.trim() || !formData.supplier_email.includes("@")) {
      newErrors.supplier_email = "Please enter a valid supplier email";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validate()) {
      onSubmit({
        payment_amount: parseFloat(formData.payment_amount),
        payment_due_date: formData.payment_due_date,
        supplier_name: formData.supplier_name,
        supplier_email: formData.supplier_email,
      });
    }
  };

  const getFieldStatus = (field: keyof typeof formData) => {
    const originalValue = parsedData[field];
    if (originalValue !== null && originalValue !== undefined) {
      return "parsed";
    }
    return formData[field] ? "filled" : "empty";
  };

  const renderFieldIcon = (status: string) => {
    if (status === "parsed") {
      return <CheckCircle2 className="w-4 h-4 text-success" />;
    }
    return <AlertCircle className="w-4 h-4 text-warning" />;
  };

  return (
    <Card className="glass-card p-6 animate-slide-up">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-lg font-display font-semibold text-foreground">
            Complete Invoice Details
          </h3>
          <p className="text-sm text-muted-foreground mt-1">
            Some fields couldn't be parsed automatically. Please fill in the missing information.
          </p>
        </div>
        <Button variant="ghost" size="icon" onClick={onCancel}>
          <X className="w-5 h-5" />
        </Button>
      </div>

      <form onSubmit={handleSubmit} className="space-y-5">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <Label htmlFor="payment_amount">Payment Amount</Label>
              {renderFieldIcon(getFieldStatus("payment_amount"))}
            </div>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">
                $
              </span>
              <Input
                id="payment_amount"
                type="number"
                step="0.01"
                min="0"
                value={formData.payment_amount}
                onChange={(e) => setFormData({ ...formData, payment_amount: e.target.value })}
                className="pl-7"
                placeholder="0.00"
              />
            </div>
            {errors.payment_amount && (
              <p className="text-xs text-destructive">{errors.payment_amount}</p>
            )}
          </div>

          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <Label htmlFor="payment_due_date">Payment Due Date</Label>
              {renderFieldIcon(getFieldStatus("payment_due_date"))}
            </div>
            <Input
              id="payment_due_date"
              type="date"
              value={formData.payment_due_date}
              onChange={(e) => setFormData({ ...formData, payment_due_date: e.target.value })}
            />
            {errors.payment_due_date && (
              <p className="text-xs text-destructive">{errors.payment_due_date}</p>
            )}
          </div>

          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <Label htmlFor="supplier_name">Supplier Name</Label>
              {renderFieldIcon(getFieldStatus("supplier_name"))}
            </div>
            <Input
              id="supplier_name"
              type="text"
              value={formData.supplier_name}
              onChange={(e) => setFormData({ ...formData, supplier_name: e.target.value })}
              placeholder="Enter supplier name"
            />
            {errors.supplier_name && (
              <p className="text-xs text-destructive">{errors.supplier_name}</p>
            )}
          </div>

          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <Label htmlFor="supplier_email">Supplier Email</Label>
              {renderFieldIcon(getFieldStatus("supplier_email"))}
            </div>
            <Input
              id="supplier_email"
              type="email"
              value={formData.supplier_email}
              onChange={(e) => setFormData({ ...formData, supplier_email: e.target.value })}
              placeholder="supplier@example.com"
            />
            {errors.supplier_email && (
              <p className="text-xs text-destructive">{errors.supplier_email}</p>
            )}
          </div>
        </div>

        <div className="flex gap-3 pt-4">
          <Button type="button" variant="outline" onClick={onCancel} className="flex-1">
            Cancel
          </Button>
          <Button
            type="submit"
            className="flex-1 gradient-primary text-primary-foreground shadow-primary"
          >
            Submit Invoice
          </Button>
        </div>
      </form>
    </Card>
  );
}
